/**
 * Maps a language tag to the Intl locale that resolves to the RIGHT
 * NUMBERING SYSTEM for it - specifically, that a bare `'ar'` resolves to
 * `latn` (plain "3") under `Intl.NumberFormat`, while only the
 * region-qualified `'ar-EG'` resolves to `arab` ("٣"). This app's i18next
 * language tag is the bare `'ar'`, so without this map every Arabic-Indic
 * digit anywhere in the app would silently print in Latin script - see
 * `lib/i18n.ts`'s own comment on its `number` formatter override for the
 * full story.
 *
 * Pulled out of `lib/i18n.ts` into its OWN pure module - no import of
 * `i18next`, `react-i18next` or anything that touches a browser global - so
 * a pure, offline-loadable module (`lib/customerAnswers.ts`, bundled with no
 * DOM by `scripts/verify-customer-services.js`) can resolve the identical
 * locale without dragging in the whole i18n bootstrap. `i18n.init()` runs as
 * a side effect of importing `lib/i18n.ts` and touches `window`/`navigator`
 * through `i18next-browser-languagedetector`, which a Node-run verifier has
 * neither of. `lib/i18n.ts` imports this exact table rather than keeping its
 * own copy, so there remains exactly ONE map, not two that could drift.
 */
export const NUMBER_LOCALES: Record<string, string> = { ar: 'ar-EG' }

/** The Intl locale to use for a language tag, e.g. `'ar'` -> `'ar-EG'`. */
export const numberLocaleFor = (language: string): string => NUMBER_LOCALES[language] || language

/**
 * Wraps a FORMATTED FIGURE in FIRST STRONG ISOLATE (U+2068) ... POP
 * DIRECTIONAL ISOLATE (U+2069), so its base direction comes from its own first
 * strong character and NOT from whatever `dir` the container happens to carry.
 *
 * WHY THIS EXISTS, measured 2026-08-25. `Intl.NumberFormat('ar-EG')` returns a
 * currency string that OPENS with U+200F RIGHT-TO-LEFT MARK and mixes three
 * scripts. Under an RTL base its runs lay out (visually, left to right) as
 * USD | million | 3.2, which an Arabic reader reads figure-first, code-last -
 * ICU's own intended order. Under an LTR base the SAME string lays out
 * million | 3.2 | USD and reads code-first. Both orders were measured per run
 * with `Range.getBoundingClientRect` on the two screens that print this exact
 * value: the Marketplace's Live Tender Signal (no `dir`, so RTL) and the Tender
 * Center's headline stat, which goes through the SHARED
 * `components/portal/MetricStrip.tsx` - and that component pins `dir="ltr"` on
 * the value for its own good reason (a bare number must not be stranded at the
 * far side of a right-aligned Arabic card). MetricStrip is shared with the
 * DEPLOYED supplier portal, so the fix cannot live there; it lives here, where
 * the figure is built. An isolate is what makes the ambient `dir` irrelevant,
 * which is the only way two screens printing one value can be made to agree.
 *
 * NOT `dir="rtl"` and not a hardcoded RLI (U+2067): the SAME helper serves
 * English, where the string opens with "USD" and must stay LTR. FSI reads the
 * first strong character - the RLM in Arabic, the "U" in English - so one call
 * is correct in both languages with no language argument. Arabic-Indic digits
 * are AN, not strong, so a digits-first string like a localised "6.4 days"
 * still resolves RTL from its Arabic word: this is right for any formatted
 * figure, not only for currency.
 *
 * The characters are invisible but real: anything comparing this output to a
 * plain string must strip them. They are written as `\u....` ESCAPES rather
 * than pasted literals for the same reason - a literal FSI leaves no visible
 * trace in the source, so an editor, a merge or a find-and-replace can delete
 * one without anybody seeing it go.
 */
export const bidiIsolate = (text: string): string => '\u2068' + text + '\u2069'

/**
 * Formats a USD amount with the ISO CODE, not the "$" symbol
 * (`currencyDisplay: 'code'`), in the given language's number locale, and
 * strips the NO-BREAK SPACE (U+00A0) Intl inserts between the code and the
 * figure in both locales (confirmed against the live Intl implementation).
 * Left in place, that NBSP glues the code and the number into one
 * unbreakable run, which silently defeats a narrow column's ability to wrap
 * the figure instead of clipping it.
 *
 * Pulled out of two call sites (the Tender Center's headline "open value"
 * stat and its portfolio cards' per-tender value) that both did all three of
 * these things - resolve the number locale, format as a USD code-display
 * currency, strip the NBSP - so there is exactly ONE place this can drift
 * from, not two. `options` carries only what differs between them (a
 * compact headline figure vs. a card's exact one). The Marketplace's Live
 * Tender Signal panel (`SourcingRail.tsx`) is the THIRD call site as of
 * 2026-08-25 - it hand-rolled the same three steps, minus the NBSP strip,
 * which is exactly the drift this function's existence is meant to prevent.
 *
 * The result is BIDI-ISOLATED (see `bidiIsolate` above): the code/figure order
 * must be decided by ICU, never by whichever container the value happens to
 * land in. Without it, this one value read figure-first on
 * /dashboard/marketplace and code-first on /dashboard/tenders - the same
 * string, on two screens, read two different ways.
 */
export const formatUsdCode = (
  value: number,
  language: string,
  options: Pick<Intl.NumberFormatOptions, 'notation' | 'maximumFractionDigits'>
): string =>
  bidiIsolate(new Intl.NumberFormat(numberLocaleFor(language), {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'code',
    ...options
  }).format(value).replace(/\u00A0/g, ' '))
